import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Close as CloseIcon, Link as LinkIcon, Replay, Visibility } from '@mui/icons-material';
import { GridRenderCellParams } from '@mui/x-data-grid';
import { Header } from '../Components/Header/Header';
import { ColumnDef, DataTable } from '../Components/Table/DataTable';
import { SearchableHeader } from '../Components/Search/SearchableHeader';
import { PageContainer } from '../Styles/AppStyles';
import useGetCustomData from '../Hooks/useGetCustomData';
import { useColumnSearches } from '../Hooks/useColumnSearch';
import apiClient from '../Services/ApiClient';

interface FailedOrder {
  OIDRef: number;
  OID: string;
  POID: string | null;
  OrderAmount: number;
  CreatedAt: string;
  RetryCount: number;
  P21ResponseMessage: string | null;
  P21VendingLocation: string;
  P21Customer: string;
  Username: string;
}

type FailedOrderTableRow = FailedOrder & { _actions?: never };

type FailedOrderAction = 'retry' | 'link';

// Mirrors the API check: P21 order numbers are numeric and must fit the sync service's Int32 parse.
const isValidP21OrderNumber = (value: string) => /^[0-9]{1,10}$/.test(value) && Number(value) <= 2147483647;

interface OrderItem {
  itemId: number;
  productName: string;
  productCode: string;
  orderedQty: number;
  unitPrice: number;
  amount: number;
  salesPricingUnit: string;
  currentStock: number;
  productType: 'AbatixProduct' | 'NonAbatixProduct';
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const FailedOrdersPage: React.FC = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);

  const { columnSearches, setSearch, clearSearch } = useColumnSearches([
    'OID', 'P21Customer', 'P21VendingLocation', 'Username', 'POID',
  ]);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<FailedOrder | null>(null);
  const [items, setItems] = useState<OrderItem[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);

  const [refreshKey, setRefreshKey] = useState(0);
  const [actionType, setActionType] = useState<FailedOrderAction | null>(null);
  const [actionOrder, setActionOrder] = useState<FailedOrder | null>(null);
  const [p21OrderNumber, setP21OrderNumber] = useState('');
  const [actionSubmitting, setActionSubmitting] = useState(false);
  const [actionError, setActionError] = useState('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const url = useMemo(() => {
    const params = new URLSearchParams();
    params.set('page', String(page + 1));
    params.set('pageSize', String(pageSize));
    if (refreshKey) params.set('_', String(refreshKey));
    Object.entries(columnSearches).forEach(([field, value]) => {
      if (value.trim()) params.set(field, value.trim());
    });
    return `/api/failed-orders?${params.toString()}`;
  }, [page, pageSize, refreshKey, columnSearches]);

  const [data, loading] = useGetCustomData(url);

  const orders: FailedOrder[] = data?.orders ?? [];
  const totalCount: number = data?.totalCount ?? 0;

  const handleViewItems = async (order: FailedOrder) => {
    setSelectedOrder(order);
    setModalOpen(true);
    setItemsLoading(true);
    setItems([]);
    try {
      const response = await apiClient(`/api/failed-orders/${order.OIDRef}/items`);
      const json = await response.json();
      setItems(json.items ?? []);
    } catch {
      setItems([]);
    } finally {
      setItemsLoading(false);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedOrder(null);
    setItems([]);
  };

  const handleOpenAction = (type: FailedOrderAction, order: FailedOrder) => {
    setActionType(type);
    setActionOrder(order);
    setP21OrderNumber('');
    setActionError('');
  };

  const handleCloseAction = () => {
    if (actionSubmitting) return;
    setActionType(null);
    setActionOrder(null);
  };

  const handleSubmitAction = async () => {
    if (!actionType || !actionOrder) return;

    const trimmedNumber = p21OrderNumber.trim();
    if (actionType === 'link' && !isValidP21OrderNumber(trimmedNumber)) {
      setActionError('Enter a numeric P21 Order Number.');
      return;
    }

    setActionSubmitting(true);
    setActionError('');
    try {
      const response = await apiClient(
        `/api/failed-orders/${actionOrder.OIDRef}/${actionType === 'retry' ? 'retry' : 'link-p21'}`,
        {
          method: 'POST',
          ...(actionType === 'link' ? { body: JSON.stringify({ p21OrderNumber: trimmedNumber }) } : {}),
        }
      );
      const json = await response.json().catch(() => ({}));

      if (response.ok && json.isSuccess) {
        setSnackbar({ open: true, message: `${actionOrder.OID}: ${json.message}`, severity: 'success' });
        setActionType(null);
        setActionOrder(null);
        setRefreshKey((k) => k + 1);
      } else if (response.status === 409 || response.status === 404) {
        // Already resolved elsewhere (sync service or another admin) — drop it from the list.
        setSnackbar({ open: true, message: json.message || 'Order is no longer failed.', severity: 'error' });
        setActionType(null);
        setActionOrder(null);
        setRefreshKey((k) => k + 1);
      } else {
        setActionError(json.message || 'Request failed. Please try again.');
      }
    } catch {
      setActionError('Request failed. Please try again.');
    } finally {
      setActionSubmitting(false);
    }
  };

  const columns: ColumnDef<FailedOrderTableRow>[] = [
    {
      field: '_actions',
      headerName: '',
      width: 120,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <>
          <Tooltip title="View items">
            <IconButton size="small" onClick={() => handleViewItems(params.row as FailedOrder)}>
              <Visibility fontSize="small" sx={{ color: '#64748b' }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Retry push to P21">
            <IconButton size="small" onClick={() => handleOpenAction('retry', params.row as FailedOrder)}>
              <Replay fontSize="small" sx={{ color: '#008490' }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Enter P21 Order Number manually">
            <IconButton size="small" onClick={() => handleOpenAction('link', params.row as FailedOrder)}>
              <LinkIcon fontSize="small" sx={{ color: '#008490' }} />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
    {
      field: 'OID',
      headerName: 'Transaction ID',
      width: 160,
      renderHeader: () => <SearchableHeader field="OID" headerName="Transaction ID" searchValue={columnSearches.OID} onSearchChange={setSearch} onClearSearch={clearSearch} />,
    },
    {
      field: 'CreatedAt',
      headerName: 'Order Date',
      width: 160,
    },
    {
      field: 'POID',
      headerName: 'PO / Job #',
      width: 130,
      renderHeader: () => <SearchableHeader field="POID" headerName="PO / Job #" searchValue={columnSearches.POID} onSearchChange={setSearch} onClearSearch={clearSearch} />,
    },
    {
      field: 'P21Customer',
      headerName: 'Account',
      width: 220,
      renderHeader: () => <SearchableHeader field="P21Customer" headerName="Account" searchValue={columnSearches.P21Customer} onSearchChange={setSearch} onClearSearch={clearSearch} />,
    },
    {
      field: 'P21VendingLocation',
      headerName: 'Location',
      width: 230,
      renderHeader: () => <SearchableHeader field="P21VendingLocation" headerName="Location" searchValue={columnSearches.P21VendingLocation} onSearchChange={setSearch} onClearSearch={clearSearch} />,
    },
    {
      field: 'Username',
      headerName: 'Employee',
      width: 150,
      renderHeader: () => <SearchableHeader field="Username" headerName="Employee" searchValue={columnSearches.Username} onSearchChange={setSearch} onClearSearch={clearSearch} />,
    },
    {
      field: 'OrderAmount',
      headerName: 'Amount',
      width: 110,
      renderCell: (params: GridRenderCellParams) =>
        currencyFormatter.format(params.value ?? 0),
    },
    {
      field: 'RetryCount',
      headerName: 'Retries',
      width: 80,
    },
    {
      field: 'P21ResponseMessage',
      headerName: 'Failure Reason',
      width: 340,
      renderCell: (params: GridRenderCellParams) => {
        const message: string = params.value ?? '';
        if (!message) return null;
        return (
          <Tooltip title={message} placement="top-start">
            <Typography
              variant="body2"
              noWrap
              sx={{ fontSize: '0.8rem', color: '#d32f2f', cursor: 'default' }}
            >
              {message}
            </Typography>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <PageContainer>
      <Header title="Failed Orders" count={totalCount} />
      <DataTable
        data={orders}
        columns={columns}
        title="Failed Orders"
        pageKey="failed-orders"
        loading={loading}
        serverSidePagination
        page={page}
        idField="OIDRef"
        rowCount={totalCount}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        initialPageSize={pageSize}
      />

      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pr: 6, fontSize: '1rem', fontWeight: 600 }}>
          Items — {selectedOrder?.OID}
          {selectedOrder?.P21ResponseMessage && (
            <Typography
              variant="body2"
              sx={{ mt: 0.5, fontSize: '0.78rem', color: '#d32f2f', fontWeight: 400 }}
            >
              {selectedOrder.P21ResponseMessage}
            </Typography>
          )}
          <IconButton
            onClick={handleCloseModal}
            size="small"
            sx={{ position: 'absolute', right: 12, top: 12 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          {itemsLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
              <CircularProgress size={32} />
            </div>
          ) : items.length === 0 ? (
            <Typography sx={{ p: 3, color: '#64748b' }}>No items found.</Typography>
          ) : (
            (['AbatixProduct', 'NonAbatixProduct'] as const).map((type) => {
              const group = items.filter((i) => i.productType === type);
              if (group.length === 0) return null;
              const groupTotal = group.reduce((s, i) => s + Number(i.amount), 0);
              return (
                <React.Fragment key={type}>
                  <Typography
                    sx={{
                      px: 2,
                      py: 1,
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      color: '#fff',
                      backgroundColor: '#008490',
                      letterSpacing: '0.04em',
                    }}
                  >
                    {type === 'AbatixProduct' ? 'Abatix Products' : 'Non-Abatix Products'}
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.75rem', backgroundColor: '#f0fafb' } }}>
                        <TableCell>Product</TableCell>
                        <TableCell>SKU</TableCell>
                        <TableCell align="center">Ordered</TableCell>
                        <TableCell align="center">In Stock</TableCell>
                        <TableCell align="right">Unit Price</TableCell>
                        <TableCell align="right">Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {group.map((item) => {
                        const isLowStock = item.currentStock < item.orderedQty;
                        return (
                          <TableRow
                            key={`${item.productType}-${item.itemId}`}
                            sx={{
                              backgroundColor: isLowStock ? '#fff5f5' : 'inherit',
                              '&:hover': { backgroundColor: isLowStock ? '#ffe8e8' : '#f5f5f5' },
                            }}
                          >
                            <TableCell
                              sx={{
                                fontWeight: isLowStock ? 600 : 400,
                                color: isLowStock ? '#c62828' : 'inherit',
                                fontSize: '0.82rem',
                              }}
                            >
                              {item.productName}
                              {isLowStock && (
                                <Chip
                                  label="Low Stock"
                                  size="small"
                                  sx={{
                                    ml: 1,
                                    height: 18,
                                    fontSize: '0.65rem',
                                    backgroundColor: '#ffcdd2',
                                    color: '#c62828',
                                  }}
                                />
                              )}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.82rem', color: '#555' }}>
                              {item.productCode}
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: '0.82rem' }}>
                              {item.orderedQty}
                              {item.salesPricingUnit ? ` ${item.salesPricingUnit}` : ''}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                fontSize: '0.82rem',
                                fontWeight: isLowStock ? 700 : 400,
                                color: isLowStock ? '#c62828' : '#2e7d32',
                              }}
                            >
                              {item.currentStock}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.82rem' }}>
                              {currencyFormatter.format(item.unitPrice)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.82rem' }}>
                              {currencyFormatter.format(item.amount)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow sx={{ backgroundColor: '#008490' }}>
                        <TableCell colSpan={5} sx={{ color: '#fff', fontWeight: 700, fontSize: '0.8rem' }}>
                          Total Amount
                        </TableCell>
                        <TableCell align="right" sx={{ color: '#fff', fontWeight: 700, fontSize: '0.8rem' }}>
                          {currencyFormatter.format(groupTotal)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </React.Fragment>
              );
            })
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!actionType}
        onClose={handleCloseAction}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 600 }}>
          {actionType === 'retry' ? 'Retry push to P21' : 'Enter P21 Order Number'} — {actionOrder?.OID}
        </DialogTitle>
        <DialogContent dividers>
          {actionType === 'retry' ? (
            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
              This puts the order back in the P21 sync queue with its retry count reset. The sync
              service will send it on its next run. If P21 rejects it again, it will come back to
              this page with the new error.
            </Typography>
          ) : (
            <>
              <Typography variant="body2" sx={{ fontSize: '0.85rem', mb: 2 }}>
                Use this when the order already exists in P21. The order will be marked as synced
                with this P21 Order Number, removed from this page, and handled like any other
                synced transaction. It will <strong>not</strong> be sent to P21.
              </Typography>
              <TextField
                autoFocus
                fullWidth
                size="small"
                label="P21 Order Number"
                value={p21OrderNumber}
                onChange={(e) => {
                  setP21OrderNumber(e.target.value);
                  setActionError('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSubmitAction();
                }}
                inputProps={{ inputMode: 'numeric', maxLength: 10 }}
                disabled={actionSubmitting}
              />
            </>
          )}
          {actionError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {actionError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAction} disabled={actionSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitAction}
            disabled={actionSubmitting || (actionType === 'link' && !p21OrderNumber.trim())}
            sx={{ backgroundColor: '#008490', '&:hover': { backgroundColor: '#006d77' } }}
          >
            {actionSubmitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : actionType === 'retry' ? 'Retry' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default FailedOrdersPage;
